import pick from 'lodash/pick';
import omit from 'lodash/omit';
import { StyleBindings } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import {
  getStylePropertySchema,
  layoutStylePropertyNames,
  StyleProperty,
  stylePropertyNames,
} from './style-properties';
import { getBlankBindingForSchema } from './bindings';
import type { Style, StyleWithShorthands } from '../composer/styles/style-binding';
import { ComponentName } from './design-system';
import type ComponentInstanceProxy from '../composer/dolanruntime/ComponentInstanceProxy';

// List of style properties in which values should be numeric instead of strings.
const numericStyleProperties = ['flexShrink', 'flexGrow'];

/**
 * Fixes the style values in the given object by migrating
 * them from any legacy format to the preferred ones.
 * If nothing to change then returns the object as is.
 * @param {Object} style mapping of style values/bindings by the property name
 */
export function migrateStyles(style: StyleBindings): StyleBindings {
  if (!style) return {};
  let result = style;
  // Migrate 'flex' shortcut to explicit style properties
  // However, preserve `flex: 1` which is a special case in case of the layout root component.
  // Runtime will use this for "Stretch to the viewport height" effect
  if (result.flex != null && result.flex !== 1) {
    const [flexGrow, flexShrink, flexBasis] = String(result.flex).split(' ');
    // NOTE: The default for 'flex' is '0 1 auto'
    result = { ...result };
    if (result.flexGrow == null) result.flexGrow = flexGrow || 0;
    if (result.flexShrink == null) result.flexShrink = flexShrink || 1;
    if (result.flexBasis == null) result.flexBasis = flexBasis || null; // = auto
    delete result.flex;
  }

  (Object.keys(result) as Array<keyof StyleWithShorthands>).forEach((propName) => {
    let value = result[propName];
    // At some point `alignItems` could have become `start` instead of `flex-start`.
    // Fix those values.
    if (propName === 'alignItems' && value === 'start') {
      value = 'flex-start';
    }
    // Convert numeric style properties to actual numbers, instead of strings
    if (numericStyleProperties.includes(propName) && typeof value === 'string') {
      value = parseFloat(value);
    }
    // Delete nully values
    if (value == null) {
      result = { ...result };
      delete result[propName];
    } else if (value !== result[propName]) {
      result = { ...result, [propName]: value };
    }
  });

  return result;
}

export const stylePropertyFallbackBindings: StyleBindings = Object.fromEntries(
  stylePropertyNames.map((propName) => {
    const schema = getStylePropertySchema(propName);
    const binding = 'default' in schema && schema.default !== undefined ? getBlankBindingForSchema(schema, []) : null;
    return [propName, binding] as const;
  }),
);

export const compositeComponentEnabledOuterStyles = {
  flexGrow: true,
  flexShrink: true,
  flexBasis: true,
  alignSelf: true,
  aspectRatio: true,
  bottom: true,
  height: true,
  left: true,
  margin: true,
  marginBottom: true,
  marginLeft: true,
  marginRight: true,
  marginTop: true,
  maxHeight: true,
  maxWidth: true,
  minHeight: true,
  minWidth: true,
  position: true,
  right: true,
  top: true,
  width: true,
  zIndex: true,
  opacity: true,
  display: true,
  backfaceVisibility: true,
  elevation: true,
  scrollDisabled: true,
  safeAreaDisabled: true,
  gapEnabled: true,
  gapAfter: true,
  dimensionsBehavior: true,
  widthBehavior: true,
  heightBehavior: true,
};
export const compositeComponentEnabledInnerStyles = {
  flexDirection: true,
  flexWrap: true,
  justifyContent: true,
  alignItems: true,
  alignContent: true,
  borderRightColor: true,
  borderBottomColor: true,
  borderBottomLeftRadius: true,
  borderBottomRightRadius: true,
  borderBottomWidth: true,
  borderColor: true,
  borderLeftColor: true,
  borderLeftWidth: true,
  borderRadius: true,
  borderRightWidth: true,
  borderStyle: true,
  borderTopColor: true,
  borderTopLeftRadius: true,
  borderTopRightRadius: true,
  borderTopWidth: true,
  borderWidth: true,
  backgroundColor: true,
  shadowEnabled: true,
  shadowOffsetWidth: true,
  shadowOffsetHeight: true,
  shadowRadius: true,
  shadowColor: true,
  padding: true,
  paddingBottom: true,
  paddingLeft: true,
  paddingRight: true,
  paddingTop: true,
  overflow: true,
};

const pageEnabledStyleProperties: Partial<Record<StyleProperty, boolean>> = {
  paddingLeft: true,
  paddingRight: true,
  paddingTop: true,
  paddingBottom: true,
  backgroundColor: true,
  scrollDisabled: true,
  safeAreaDisabled: true,
  alignItems: true,
  // The only component that has `flex` enabled instead of `flexGrow`/`flexShrink`/`flexBasis`
  // is the page layout root component (meaning "Stretch to the viewport height")
  flex: true,
};

function migrateEnabledStyleProperties(visibilities: Partial<Record<StyleProperty, boolean>> = {}) {
  let migrated = visibilities || {};
  if (visibilities.padding) {
    migrated = {
      ...migrated,
      paddingLeft: true,
      paddingRight: true,
      paddingTop: true,
      paddingBottom: true,
    };
  }
  if (visibilities.margin) {
    migrated = {
      ...migrated,
      marginLeft: true,
      marginRight: true,
      marginTop: true,
      marginBottom: true,
    };
  }
  if (visibilities.marginLeft || visibilities.marginTop || visibilities.marginRight || visibilities.marginBottom) {
    migrated = {
      ...migrated,
      gapEnabled: true,
      gapAfter: true,
    };
  }
  return migrated;
}

const rowComponentEnabledStyleOverrides = {
  flexDirection: false,
  justifyContent: false,
  alignItems: false,
  flexWrap: false,
  alignContent: false,
};

const rowCellComponentEnabledStyleOverrides = {
  width: false,
  minWidth: false,
  maxWidth: false,
  height: false,
  minHeight: false,
  maxHeight: false,
  flexGrow: false,
  flexShrink: false,
  flexBasis: false,
  flexDirection: false,
  flexWrap: false,
  alignContent: false,
  alignSelf: false,
  margin: false,
  marginTop: false,
  marginLeft: false,
  marginRight: false,
  marginBottom: false,
  position: false,
  left: false,
  right: false,
  top: false,
  bottom: false,
  zIndex: false,
};

const rowComponentNames = ['fixed_column_layout', 'responsive_column_layout', 'layout'];

export const defaultThemeVariablesByProperty = {
  gapAfter: '$spacing_component_L',
};

/**
 * Returns a mapping of style property names to boolean values
 * indicating whether or not the style property is editable in the current
 * component's context.
 */
export default function getEnabledStyleProperties(
  component: ComponentInstanceProxy | null | undefined,
  parentComponent: ComponentInstanceProxy | null | undefined,
  isolatedComponentId?: string | null,
): Partial<Record<StyleProperty, boolean>> {
  if (!component) {
    // Editing a page
    return pageEnabledStyleProperties;
  }
  if (isolatedComponentId === component.id) {
    return migrateEnabledStyleProperties(compositeComponentEnabledInnerStyles);
  }
  if (component.description.composite_json) {
    return migrateEnabledStyleProperties(compositeComponentEnabledOuterStyles);
  }
  // Editing a selected component
  const { description } = component;
  const styleProperties = description?.reactnative_style_properties;
  let enabledProperties = migrateEnabledStyleProperties(styleProperties?.visibilities);
  if (rowComponentNames.includes(component?.name)) {
    enabledProperties = { ...enabledProperties, ...rowComponentEnabledStyleOverrides };
  }
  if (parentComponent?.name && rowComponentNames.includes(parentComponent.name)) {
    enabledProperties = { ...enabledProperties, ...rowCellComponentEnabledStyleOverrides };
  }
  const overflowDisabled = [ComponentName.ScrollView, ComponentName.RecyclerView].includes(
    component?.name as ComponentName,
  );
  if (overflowDisabled) {
    enabledProperties = omit(enabledProperties, 'overflow');
  }
  return enabledProperties;
}

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where only layout related styles are preserved, the rest of them are omitted.
 */
export function pickLayoutStyles<T extends StyleWithShorthands | Style | StyleBindings>(styles: T): T {
  return pick(styles, layoutStylePropertyNames) as T;
}

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where all layout related styles are omitted, and the rest are preserved.
 */
export function omitLayoutStyles<T extends StyleWithShorthands | Style | StyleBindings>(styles: T): T {
  return omit(styles, layoutStylePropertyNames) as T;
}

const compositeComponentOuterStyles = Object.keys(compositeComponentEnabledOuterStyles);

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where only styles that are allowed on the OUTSIDE of a composite root component.
 */
export function pickCompositeRootOuterStyles<T extends StyleWithShorthands | Style | StyleBindings | undefined>(
  styles: T,
): T {
  return pick(styles, compositeComponentOuterStyles) as T;
}

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where only styles that are allowed on the INSIDE of a composite root component.
 */
export function pickCompositeRootInnerStyles<T extends StyleWithShorthands | Style | StyleBindings | undefined>(
  styles: T,
): T {
  return omit(styles, compositeComponentOuterStyles) as T;
}

const pageStyleProperties = ['backgroundColor', 'scrollDisabled', 'safeAreaDisabled'] satisfies StyleProperty[];

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where only styles that are allowed on directly on a view (not on the root component).
 */
export function pickViewStyleProperties<T extends StyleWithShorthands | Style | StyleBindings | undefined>(
  styles: T,
): T {
  return pick(styles, pageStyleProperties) as T;
}

/**
 * Takes a mapping of styles to their values or bindings and returns a mapping
 * where styles not allowed on the view root component are omitted.
 */
export function omitViewStyleProperties<T extends StyleWithShorthands | Style | StyleBindings | undefined>(
  styles: T,
): T {
  return omit(styles, pageStyleProperties) as T;
}

const transparentColors = ['transparent', 'rgba(255,255,255,0)', 'rgba(0,0,0,0)'];

/**
 * Returns true if and only if the given style binding is either nullish
 * or it is a STATIC binding to a TRANSPARENT color value.
 * @param binding style binding
 * @returns whether the binding would resolve to a static transparent color value or nullish
 */
export function isStaticTransparentColorBinding(
  binding:
    | {
        source: string;
        value?: unknown;
        key?: unknown;
      }
    | string
    | number
    | boolean
    | null
    | undefined,
): boolean {
  if (!binding) {
    // Nullish binding is considered "transparent"
    return true;
  }
  let colorValue: string | undefined;
  if (typeof binding === 'string') {
    colorValue = binding;
  } else if (typeof binding === 'object' && binding?.source === 'str') {
    colorValue = (binding.key || binding.value) as string;
  } else {
    // Non-static string binding
    return false;
  }
  return !colorValue || transparentColors.includes(colorValue);
}
